import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollPosition, trackWindowScroll } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import CopyToClipboardButton from "../../../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import FullScreenViewer, {
	FullScreenViewerProps,
} from "../../../../../components/common/FullScreenViewer/FullScreenViewer";
import { FullScreenImageType } from "../../../../../components/common/FullScreenViewer/Image/FullScreenImage.tsx";
import BriaImage, { BriaImageProps, ImageErrorType } from "../../../../../components/common/Galleries/BriaImage";
import { useAppStore } from "../../../../../hooks/useStores";
import StructuredPromptImageCanvas from "../../../../../layout/LabsLayout/Results/Canvas/StructuredPromptImageCanvas";
import { PlaygroundImage } from "../../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../../models/text-to-image.ts";
import { isFiboApp } from "../../../../../utils/index.ts";
import useImageUtils from "../../../../../utils/useImageUtils.tsx";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import styles from "./TextToImageGallery.module.scss";

// Extracted component to properly use hooks
type TextToImageGalleryItemProps = {
	image: BriaImageProps & PlaygroundImage;
	index: number;
	gridView: boolean;
	scrollPosition: ScrollPosition;
	onOpenFullScreen: (index: number) => void;
};

const TextToImageGalleryItem = observer(
	({ image, index, gridView, scrollPosition, onOpenFullScreen }: TextToImageGalleryItemProps) => {
		const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage" });
		const { textToImageStore, playgroundStore, fiboStore } = useAppStore();
		const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
		const sentPostMessageResultIds = useRef<Set<string>>(new Set());

		const isFiboPro = (image.config as TextToImageConfigType)?.model === "fibo";
		const isFiboLite = (image.config as TextToImageConfigType)?.model_id === "fibo_lite";

		return (
			<span
				key={image.id || index}
				className={styles.imageWrapper}
				ref={(el) => {
					if (index === 0 && el) {
						playgroundStore.lastResultImage = { element: el as HTMLElement, imageId: image.id };
					}
				}}
			>
				<BriaImage
					key={index}
					{...image}
					displayOverlay={textToImageStore.isRefining ? "on" : image.displayOverlay || "customHover"}
					errorType={image.error?.type}
					handleFullScreen={() => onOpenFullScreen(index)}
					lazyImageProps={{
						...image.lazyImageProps,
						scrollPosition: scrollPosition,
						onLoad: (e?: React.SyntheticEvent<HTMLImageElement, Event>) => {
							playgroundStore.addLoadedImageToStore(image);
							if (e?.currentTarget) {
								setImageElement(e.currentTarget);
							}
						},
					}}
					fullScreenProps={image.fullScreenProps}
					image={image}
					hideDownloadIcon={false}
					gridView={gridView}
					{...((isFiboPro || image.status_url || isFiboApp()) && {
						asyncProps: {
							isV2: true,
							statusUrl: image.status_url,
							maxAttempts: 360,
							sleepSecs: 2,
							abortController: fiboStore.abortController || undefined,
							onPollingChange: fiboStore.setPollingStatus,
						},
						onSuccessPulling: (result?: any) => {
							if (result && image.status_url) {
								const foundResult = playgroundStore.playgroundResults.find((res) =>
									res.images.some((img) => img.id === image.id),
								);
								const foundImage = foundResult?.images.find((img) => img.id === image.id);

								if (foundImage) {
									foundImage.url = result.image_url;
									foundImage.seed = result.seed;
									foundImage.structured_prompt = result.structured_prompt;
									foundImage.warning = result.warning;

									const warningMessage = result?.warning?.toLowerCase();
									if (
										warningMessage &&
										(warningMessage.includes("intellectual property") ||
											warningMessage.includes("ip-protected"))
									) {
										textToImageStore.errors.generate = {
											message: "promptIpWarning",
											status: 200,
											isWarning: true,
										};
									}

									// Send iframe post message when all images in this result have URLs
									if (
										foundResult &&
										foundResult.config &&
										foundResult.images.every((img) => img.url) &&
										!sentPostMessageResultIds.current.has(foundResult.id)
									) {
										sentPostMessageResultIds.current.add(foundResult.id);
										iframeStore.sendGeneratePostMessage(
											foundResult.config as TextToImageConfigType,
											foundResult.images,
											false, // Text-to-image is always single mode
										);
									}
								}
							}
							playgroundStore.onSuccessResult(image);
						},
						onErrorPulling: (errorType: ImageErrorType) => playgroundStore.onErrorResult(image, errorType),
					})}
				/>

				{isFiboPro && !isFiboLite && image.structured_prompt && (
					<StructuredPromptImageCanvas image={image} imageElement={imageElement} />
				)}

				{textToImageStore.config?.displaySeed && !Number.isNaN(image.seed) && image.seed && !image.loading && (
					<Box className={styles.seedTextStyle}>
						<Box>{t("seed")}</Box>
						<Box className={styles.seedValue}>{image.seed}</Box>
						<CopyToClipboardButton className={styles.copyBtn} textToCopy={image.seed.toString()} />
					</Box>
				)}
			</span>
		);
	},
);

type TextToImageGalleryProps = {
	images: (BriaImageProps & PlaygroundImage)[];
	maxInLine?: number;
	className?: string;
	fullScreenProps?: Partial<FullScreenViewerProps>;
	scrollPosition: ScrollPosition;
	gridView?: boolean;
};

const TextToImageGallery = ({
	images,
	maxInLine = 4,
	className,
	fullScreenProps,
	scrollPosition,
	gridView = false,
}: TextToImageGalleryProps) => {
	const { playgroundStore } = useAppStore();
	const { exportImage } = useImageUtils();
	const [imageToView, setImageToView] = useState(0);
	const [isViewerOpen, setIsViewerOpen] = useState<boolean | undefined>(fullScreenProps?.open);
	const [displayMode, setDisplayMode] = useState(fullScreenProps?.displayMode);
	const fullScreenImages = images
		.filter((image) => !image.hideFullScreenButton && !image.errorType && !image.error)
		.map(({ ImageOverlay, hideApiReferenceButton, ...rest }) => ({
			src: rest.url,
			...rest,
			...rest.fullScreenProps,
		}));
	useEffect(() => {
		setImageToView(0);
	}, [images.length]);

	useEffect(() => {
		setIsViewerOpen(fullScreenProps?.open);
	}, [fullScreenProps?.open]);

	const handleOpenFullScreen = (index: number) => {
		setDisplayMode("arrows");
		const fullScreenIndex = fullScreenImages.findIndex((image) => image.id == images[index].id);
		setImageToView(fullScreenIndex);
		setIsViewerOpen(true);
	};

	const handleCloseFullScreen = () => {
		setIsViewerOpen(false);
		fullScreenProps?.onClose?.();
		setDisplayMode(fullScreenProps?.displayMode);
	};

	const instantDownloadImage = async (img: BriaImageProps & PlaygroundImage) => {
		await exportImage(
			img.url,
			img.downloadProps?.fileName || playgroundStore.getFileName(img as PlaygroundImage),
			1,
			"download",
		);
	};

	const cols = gridView ? (images.length <= 1 ? 1 : 2) : undefined;
	const rows = gridView ? Math.ceil(images.length / cols!) : undefined;
	const centerOddLast = gridView && images.length > 1 && images.length % 2 === 1;

	return (
		<>
			<Box
				className={clsx(className, styles.ttiGallery, {
					[styles.gridView]: gridView,
					[styles.centerOddLast]: centerOddLast,
				})}
				sx={{
					...(gridView
						? {
								gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
								gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
							}
						: { "& > span": { width: `calc(${100 / maxInLine}% - 0.8vw)` } }),
				}}
			>
				{images.map((image, index) => (
					<TextToImageGalleryItem
						key={image.id || index}
						image={image}
						index={index}
						gridView={gridView}
						scrollPosition={scrollPosition}
						onOpenFullScreen={handleOpenFullScreen}
					/>
				))}
			</Box>
			{isViewerOpen && (
				<FullScreenViewer
					{...fullScreenProps}
					open={isViewerOpen}
					images={fullScreenImages}
					currentIndex={imageToView}
					onClose={handleCloseFullScreen}
					displayMode={displayMode}
					onDownload={(fullScreenImages: FullScreenImageType[]) =>
						images[imageToView]?.downloadProps?.instantDownload
							? instantDownloadImage(images[imageToView])
							: playgroundStore.onFullScreenViewerDownload(images, fullScreenImages)
					}
				/>
			)}
		</>
	);
};

export default trackWindowScroll(observer(TextToImageGallery));
